import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import AppModal from '../../shared/AppModal';
import AppPopup from '../../components/apps/appsKanban/AppPopup';
import axios from '../../utils/axios';
import { getRepeatLeads, appSubmission, getCustomFields, getPipelines, registeredUserList, UpdateLeadStage } from '../../utils/urls';
import '../onboarding/onboardingAdmin.css';
import './repeatLeads.css';

const toISO = (d) => d.toLocaleDateString('en-CA');

function fmtDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatUTCto12hr(isoString) {
    if (!isoString) return '—';
    const d = new Date(isoString);
    const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    let h = d.getUTCHours();
    const m = String(d.getUTCMinutes()).padStart(2, '0');
    const period = h < 12 ? 'AM' : 'PM';
    h = h % 12 || 12;
    return `${datePart} · ${h}:${m} ${period}`;
}

function initials(name) {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '');
}

function avatarColor(name) {
    const colors = ['#4a90e2', '#2ecc71', '#e0a04a', '#9b59b6', '#e74c3c', '#1abc9c', '#3498db', '#f39c12'];
    let h = 0;
    for (let c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
    return colors[Math.abs(h) % colors.length];
}

const ROW_OPTIONS = [10, 20, 50];
const CAL_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const loanDetailsFields = ["Onedrive Link", "Gdrive Link", "Loan File ID", "Purchase Price", "Loan Amount", "Est. Taxes", "Loan Amount Status", "Closing Date", "Subject Property Address", "Sellers Concession", "First Payment Date", "Insurance Inspection Date"];
const realEstateTransactionFields = ["Real Estate Gdrive Link"];

export default function RepeatLeads() {
    const toast         = useRef(null);
    const calContainerRef = useRef(null);
    const userId = useSelector((state) => state.user.userId);

    const [dates, setDates] = useState([null, null]);
    const datesRef = useRef(dates);
    datesRef.current = dates;

    const [calOpen, setCalOpen]   = useState(false);
    const [rows, setRows]         = useState([]);
    const [total, setTotal]       = useState(0);
    const [loading, setLoading]   = useState(false);
    const [page, setPage]         = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const pageSizeRef             = useRef(10);
    pageSizeRef.current           = pageSize;
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [search, setSearch]     = useState('');

    // ── AppPopup state ──────────────────────────────────────────────────────────
    const [isOpen,          setIsOpen]          = useState(false);
    const [applicantData,   setApplicantData]   = useState(null);
    const [popupLoad,       setPopupLoad]       = useState(false);
    const [pipelineId,      setPipelineId]      = useState(1);
    const [pipelines,       setPipelines]       = useState([]);
    const [pipelineStages,  setPipelineStages]  = useState([]);
    const [allPipelinesData, setAllPipelinesData] = useState([]);
    const [inactivePipelineId, setInactivePipelineId] = useState('');
    const [stageIds,        setStageIds]        = useState('');
    const [registeredUsersList, setRegisteredUsersList] = useState([]);
    const [loanDetailsCustomFields, setLoanDetailsCustomFields] = useState([]);
    const [realEstateTransactionsCustomFields, setRealEstateTransactionsCustomFields] = useState([]);
    const [allCustomFields, setAllCustomFields] = useState([]);

    const getAllPipelines = () => {
        axios.get(`${getPipelines}?userId=${userId}`)
            .then(res => {
                if (res.status === 200) {
                    setAllPipelinesData(res.data);
                    const options = res.data.filter(p => {
                        if (p.name === 'Inactive Apps') { setInactivePipelineId(p.id); return false; }
                        return true;
                    }).map(p => ({ label: p.name, value: p.id }));
                    setPipelines(options);
                    const item = res.data.find(p => p.id === 1);
                    if (item) setPipelineStages(item.stages);
                }
            });
    };

    const getAllCustomFields = () => {
        axios.get(getCustomFields)
            .then(res => {
                if (res.status === 200) {
                    setLoanDetailsCustomFields(res.data.filter(d => loanDetailsFields.includes(d.title)));
                    setRealEstateTransactionsCustomFields(res.data.filter(d => realEstateTransactionFields.includes(d.title)));
                    setAllCustomFields(res.data.filter(d => !loanDetailsFields.includes(d.title) && !realEstateTransactionFields.includes(d.title)));
                }
            });
    };

    const handleStageUpdate = (contact_id, app_id, stage, prevStage, emails, isLoad = false, stageId = '') => {
        const payload = { userId, stage_id: stageId || stageIds[stage], email_list: emails };
        axios.post(`${UpdateLeadStage}${contact_id}/${app_id}`, payload)
            .then(res => {
                if (res.status === 200) {
                    toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Successfully Updated', life: 3000 });
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
                }
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 }));
    };

    const openPopupFn = (formId) => {
        setPipelineId(1);
        setIsOpen(true);
        setPopupLoad(true);
        getAllCustomFields();
        getAllPipelines();
        axios.get(`${appSubmission}${formId}?pipelineId=1`)
            .then(res => { if (res.status === 200) { setApplicantData(res.data); setPopupLoad(false); } });
        axios.get(registeredUserList)
            .then(res => {
                if (res.status === 200) {
                    setRegisteredUsersList(res.data.map(u => ({ label: u.name, value: u.user_id, image: u.image })));
                }
            });
    };

    const clearHoverRange = () =>
        calContainerRef.current?.querySelectorAll('.rl-hr').forEach(el => el.classList.remove('rl-hr'));

    const handleCalMouseOver = (e) => {
        if (!dates[0] || dates[1] || !calContainerRef.current) { clearHoverRange(); return; }
        const td = e.target.closest('td');
        if (!td || td.classList.contains('p-datepicker-other-month')) { clearHoverRange(); return; }
        const span = td.querySelector('span');
        if (!span || span.classList.contains('p-disabled')) { clearHoverRange(); return; }
        const day = parseInt(span.textContent);
        if (isNaN(day) || day < 1 || day > 31) { clearHoverRange(); return; }
        const monthEl = calContainerRef.current.querySelector('.p-datepicker-month');
        const yearEl  = calContainerRef.current.querySelector('.p-datepicker-year');
        if (!monthEl || !yearEl) return;
        const month = CAL_MONTHS.indexOf(monthEl.textContent.trim());
        const year  = parseInt(yearEl.textContent.trim());
        if (month === -1 || isNaN(year)) return;
        const hovered   = new Date(year, month, day);
        const start     = dates[0];
        const rStart    = hovered >= start ? start   : hovered;
        const rEnd      = hovered >= start ? hovered : start;
        calContainerRef.current.querySelectorAll('.p-datepicker-calendar td').forEach(cell => {
            cell.classList.remove('rl-hr');
            if (cell.classList.contains('p-datepicker-other-month')) return;
            const s = cell.querySelector('span');
            if (!s || s.classList.contains('p-disabled')) return;
            const d = parseInt(s.textContent);
            if (isNaN(d)) return;
            const cellDate = new Date(year, month, d);
            if (cellDate >= rStart && cellDate <= rEnd) cell.classList.add('rl-hr');
        });
    };

    /* Close calendar on outside click */
    useEffect(() => {
        if (!calOpen) return;
        const handler = () => setCalOpen(false);
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [calOpen]);

    const fetchData = useCallback((pg) => {
        const [start, end] = datesRef.current;
        if (start && !end) return;
        setLoading(true);
        const params = { page: pg, page_size: pageSizeRef.current, start_date: start ? toISO(start) : '', end_date: end ? toISO(end) : '' };
        if (userId) params.user_id = userId;
        axios.get(getRepeatLeads, { params })
            .then(res => {
                setRows(res.data.results || []);
                setTotal(res.data.count  || 0);
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load repeat leads', life: 3000 }))
            .finally(() => setLoading(false));
    }, [userId]);

    useEffect(() => { if (userId) fetchData(page); }, [page, pageSize, dates, fetchData, userId]);

    // ── Templates ──────────────────────────────────────────────────────────────
    const handleSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
        setRows(prev => [...prev].sort((a, b) => {
            const va = a[e.sortField] ?? '';
            const vb = b[e.sortField] ?? '';
            if (va === '' && vb === '') return 0;
            if (va === '') return 1;
            if (vb === '') return -1;
            if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * e.sortOrder;
            return String(va).localeCompare(String(vb)) * e.sortOrder;
        }));
    };

    const filtered = search.trim()
        ? rows.filter(r => {
            const q = search.toLowerCase();
            return (r.full_name || '').toLowerCase().includes(q)
                || (r.email || '').toLowerCase().includes(q)
                || (r.phone || '').toLowerCase().includes(q)
                || (r.live_assignment || '').toLowerCase().includes(q)
                || (r.location_name || '').toLowerCase().includes(q)
                || (r.campaign_name || '').toLowerCase().includes(q);
        })
        : rows;

    const leadTpl = (r) => {
        const name = (r.full_name || '').trim();
        const color = avatarColor(name);
        const href = r.location && r.ghl_contact_id
            ? `https://phone.bcrumbs.com/v2/location/${r.location}/contacts/detail/${r.ghl_contact_id}`
            : null;
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <div
                    className="oba-name-cell"
                    style={href ? { cursor: 'pointer', flex: 1, minWidth: 0 } : { flex: 1, minWidth: 0 }}
                    onClick={href ? () => window.open(href, '_blank') : undefined}
                >
                    <div className="oba-avatar" style={{ background: color + '22', color, borderColor: color + '44' }}>
                        {initials(name).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div className="oba-name" style={href ? { color: 'var(--oba-blue)' } : undefined}>{name || '—'}</div>
                        <div className="oba-sub">{r.email || '—'}</div>
                    </div>
                </div>
                {r.app_id && (
                    <button
                        className="rl-view-btn"
                        title="Open card"
                        onClick={e => { e.stopPropagation(); openPopupFn(r.app_id); }}
                    >
                        <span className="pi pi-eye" />
                    </button>
                )}
            </div>
        );
    };

    const phoneTpl    = (r) => <span className="oba-mono">{r.phone || '—'}</span>;

    const campaignTpl = (r) => (
        <div>
            <div className="rl-cell-main">{r.campaign_name || '—'}</div>
            <div className="oba-sub">{r.location_name || ''}</div>
        </div>
    );

    const platformTpl = (r) => (
        <div>
            <div className="rl-cell-main">{r.platform || '—'}</div>
            {r.ad_name && <div className="oba-sub" style={{ maxWidth: 200 }}>{r.ad_name}</div>}
        </div>
    );

    const countTpl = (r) => {
        const n = r.count || 0;
        return <span className={`rl-count-badge ${n >= 3 ? 'danger' : 'amber'}`}>{n}×</span>;
    };

    const latestTpl = (r) => (
        <div>
            <div className="rl-cell-main">{formatUTCto12hr(r.latest_count_date)}</div>
            <div className="oba-sub">First: {fmtDate(r.date_created)}</div>
        </div>
    );

    const assignedTpl = (r) => (
        r.live_assignment
            ? <span className="rl-cell-main">{r.live_assignment}</span>
            : <span className="oba-sub">—</span>
    );

    const appStatusTpl = (r) => {
        if (!r.app_status) return <span className="oba-sub">—</span>;
        return <span className="oba-badge oba-badge-used" style={{ whiteSpace: 'nowrap' }}>{r.app_status}</span>;
    };

    const languageTpl = (r) => (
        <span className="rl-cell-main">{r.language_name || '—'}</span>
    );

    const createdTpl = (r) => (
        <span className="rl-cell-main">{formatUTCto12hr( r.date_created)}</span>
    );

    const assignedUserTpl = (r) => (
        r.assigned_user_name
            ? <span className="rl-cell-main">{r.assigned_user_name}</span>
            : <span className="oba-sub">—</span>
    );

    const fmt = (d) => d?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });

    return (
        <div className="oba-root">
            <Toast ref={toast} />

            {/* Header */}
            {/* <div className="oba-header">
                <div>
                    <h1 className="oba-title">Repeat Leads</h1>
                    <p className="oba-subtitle">Contacts who have come back more than once within the selected date range.</p>
                </div>
            </div> */}

            {/* Toolbar — outside oba-table-wrap so the calendar dropdown isn't clipped */}
            <div className="oba-table-toolbar rl-toolbar">
                {/* Date range pill */}
                <div className="rl-datewrap" onClick={e => e.stopPropagation()}>
                    <button
                        className={`rl-datepill${calOpen ? ' open' : ''}`}
                        onClick={() => setCalOpen(v => !v)}
                    >
                        <svg className="dpi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                            <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
                            <path d="M3 9h18M8 2.5v4M16 2.5v4" />
                        </svg>
                        <span>{fmt(dates[0])} – {fmt(dates[1] || dates[0])}</span>
                        <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="m6 9 6 6 6-6" />
                        </svg>
                    </button>
                    {calOpen && (
                        <div
                            className="rl-cal-drop"
                            ref={calContainerRef}
                            onClick={e => e.stopPropagation()}
                            onMouseOver={handleCalMouseOver}
                            onMouseLeave={clearHoverRange}
                        >
                            <Calendar
                                value={dates[0] || dates[1] ? dates : null}
                                onChange={e => {
                                    const val = e.value;
                                    if (!Array.isArray(val)) return;
                                    clearHoverRange();
                                    setDates(val);
                                    setPage(1);
                                    if (val[0] && val[1]) setCalOpen(false);
                                }}
                                selectionMode="range"
                                inline
                                maxDate={new Date()}
                            />
                        </div>
                    )}
                </div>

                {/* Total count */}
                <div className="rl-total-pill">
                    <span className="rl-total-num">{total.toLocaleString()}</span>
                    <span className="rl-total-lbl">Total Repeat Leads</span>
                </div>

                {/* Search */}
                <div className="oba-search-wrap">
                    <span className="oba-search-icon pi pi-search" />
                    <input
                        className="oba-search"
                        placeholder="Search name, email, phone…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* App card popup */}
            <AppModal visible={isOpen} onHide={() => setIsOpen(false)}>
                <div style={{ marginTop: '1rem', minHeight: '70vh' }}>
                    {!popupLoad ? (
                        <AppPopup
                            isFromTable={true}
                            applicantData={applicantData}
                            pipelineId={pipelineId}
                            id={applicantData?.app_status}
                            pipelineStages={pipelineStages}
                            allPipelinesData={allPipelinesData}
                            isFromSearch={true}
                            allCustomFields={allCustomFields}
                            loanDetailsCustomFields={loanDetailsCustomFields}
                            realEstateTransactionsCustomFields={realEstateTransactionsCustomFields}
                            setApplicantData={setApplicantData}
                            handleStageUpdate={handleStageUpdate}
                            usersList={registeredUsersList}
                            userId={userId}
                            handleClose={() => setIsOpen(false)}
                            toast={toast}
                        />
                    ) : (
                        <div style={{ display: 'flex',  justifyContent: 'center', minHeight: '70vh' }}>
                            <div style={{ width: '40vw', height: '60px', borderRadius: 12 }} className="loadingAppItem" />
                        </div>
                    )}
                </div>
            </AppModal>

            {/* Table */}
            <div className="oba-table-wrap">
                <DataTable
                    value={filtered}
                    loading={loading}
                    className="oba-datatable"
                    emptyMessage="No repeat leads found."
                    lazy
                    paginator
                    rows={pageSize}
                    totalRecords={search.trim() ? filtered.length : total}
                    first={(page - 1) * pageSize}
                    onPage={e => {
                        if (e.rows !== pageSize) {
                            pageSizeRef.current = e.rows;
                            setPageSize(e.rows);
                            setPage(1);
                        } else {
                            setPage(Math.floor(e.first / pageSize) + 1);
                        }
                    }}
                    rowsPerPageOptions={ROW_OPTIONS}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                    currentPageReportTemplate="{first}–{last} of {totalRecords}"
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    scrollable
                >
                    <Column field="full_name"         header="Name"              body={leadTpl}      sortable frozen style={{ minWidth: 220 }} />
                    <Column field="phone"             header="Phone"             body={phoneTpl}     sortable style={{ minWidth: 140 }} />
                    <Column field="campaign_name"     header="Campaign"          body={campaignTpl}  sortable style={{ minWidth: 180 }} />
                    <Column field="language_name"     header="Language"          body={languageTpl}  sortable style={{ minWidth: 120 }} />
                    <Column field="count"             header="Count"             body={countTpl}     sortable style={{ minWidth: 80  }} />
                    <Column field="assigned_user_name" header="Assigned User"   body={assignedUserTpl}  sortable style={{ minWidth: 150 }} />
                    <Column field="latest_count_date" header="Latest Click Date" body={latestTpl}    sortable style={{ minWidth: 200 }} />
                    <Column field="live_assignment"   header="Live Assignment"   body={assignedTpl}  sortable style={{ minWidth: 150 }} />
                    <Column field="app_status"        header="App Status"        body={appStatusTpl} sortable style={{ minWidth: 160 }} />
                    <Column field="created_date"      header="Created Date"      body={createdTpl}        sortable style={{ minWidth: 140 }} />
                    <Column field="platform"          header="Platform / Ad"     body={platformTpl}  sortable style={{ minWidth: 160 }} />
                </DataTable>
            </div>
        </div>
    );
}
